import { Info, PlusCircle } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { View, XStack, YStack } from "tamagui";
import { ActivityUI } from "../activities/ActivityUI";
import { PlanHotelsTipsUI } from "../affiliates/booking/PlanHotelsTipsUI";
import { useStores } from "../core/stores";
import { trackEvent } from "../core/trackEvent";
import {
  HelperType,
  type HelpItem,
  HelpItemRequiredType,
} from "../helper/HelperManager";
import { useHelper } from "../helper/useHelper";
import { SectionUI } from "../sections/SectionUI";
import { Button } from "../ui/Button";
import { CenterContent } from "../ui/CenterContent";
import { Label } from "../ui/Label";
import { PopoverHelp } from "../ui/PopoverHelp";
import { UserAvatarGroup } from "../ui/UserAvatarGroup";
import { CreatorTodoListGuide } from "./CreatorTodoListGuide";

export const PlanUI = observer(({ uid }: { uid: string }) => {
  const { plansStore, sectionsStore } = useStores();
  const plan = plansStore.byId.get(uid);

  const items: HelpItem[] = React.useMemo(
    () =>
      !plan || !plan.planHasAllDataLoaded
        ? []
        : [
            {
              type: HelpItemRequiredType.Required,
              label: "Set a description by tapping in the top area",
              isCompleted: () => {
                return plan.hasDescription;
              },
            },
            {
              type: HelpItemRequiredType.Required,
              label: "Add your first card below",
              isCompleted: () => {
                return plan.moduleCount > 1; // One for planinfo
              },
            },
            {
              type: HelpItemRequiredType.Required,
              label: "Invite your first members to start the fun",
              isCompleted: () => {
                return plan.usersCount > 1;
              },
            },
          ],
    [
      plan,
      plan?.planHasAllDataLoaded,
      plan?.hasDescription,
      plan?.moduleCount,
      plan?.usersCount,
    ],
  );

  const helper = useHelper(plan?.uid ?? "plan", HelperType.Plan, items);

  if (!plan) {
    return null;
  }
  /*
    Responsive test
    $gtSm={{
          flexDirection: "row",
          flexWrap: "nowrap",
        }}
        */

  const planInfoSectionUid = plan.data.sectionIds[0];
  const planInfoSection = sectionsStore.byId.get(planInfoSectionUid);
  const planInfoModuleUid = planInfoSection?.data.moduleIds[0];

  const showAffilateBar = plan?.hasLocationModule;

  return (
    <YStack gap="$medium">
      {plan.isCreator && <CreatorTodoListGuide plan={plan} />}

      {!plan.data.sectionIds.length && (
        <CenterContent
          muted
          icon={Info}
          title={"Add your first group"}
          subTitle={"Name it and start adding cards"}
        />
      )}

      <SectionUI isFirst uid={planInfoSectionUid} />

      <UserAvatarGroup
        onPress={() => {
          plan.setShowShare(true, true);
          trackEvent("user_avatar_group_clicked");
        }}
        onSharePressed={() => {
          plan.setShowShare(true, false);
          trackEvent("user_avatar_share_clicked");
        }}
        userUids={plan.data.userIds}
      />

      {helper && <View key="helper">{helper}</View>}

      {plan.data.sectionIds.slice(1).map((sectionUid, index) => {
        return (
          <SectionUI isFirst={index === 0} key={sectionUid} uid={sectionUid} />
        );
      })}

      {plan.data.settings.useSections && (
        <Button
          type={plan.data.sectionIds.length > 0 ? "ghost" : "primary"}
          onPress={() => {
            plan.addSection({
              isDraggable: true,
              isExpandable: true,
              title: "",
            });

            trackEvent("section_create");
          }}
        >
          <YStack gap="$medium" alignItems="center">
            <XStack gap="$small" alignItems="center">
              <PlusCircle color="$muted" />
              <Label color={"$muted"}>Add section</Label>
              <PopoverHelp
                color="$muted"
                description="Create sections to group your cards, for example set a title like ´Thursday` or `When arriving` "
              />
            </XStack>
          </YStack>
        </Button>
      )}

      {showAffilateBar && <PlanHotelsTipsUI plan={plan} />}

      {planInfoModuleUid && (
        <ActivityUI marginTop={"$medium"} moduleUid={planInfoModuleUid} />
      )}
    </YStack>
  );
});
